import React, { useEffect, useRef, useCallback } from "react";
import { Container, Topbar, Button, ProgressSteps } from "@bsf/force-ui";
import { Link } from "../../router/link";
import { routes } from "../../admin/settings/routes";
import { X } from "lucide-react";
import { __ } from "@wordpress/i18n";
import Build from "./Build";
import Create from "./Create";
import Welcome from "./WelcomeNew";
import Configure from "./ExtendOnboarding";
import Features from "./Features";

// Full steps array including the hidden "Success" step
const allSteps = [
    { label: "", component: Welcome },
    { label: "", component: Configure },
    { label: "", component: Features },
    { label: "", component: Create }, // Hidden from progress bar
];

// Only visible steps for the top progress bar
const visibleSteps = allSteps.slice(0, 4); // Exclude "Success"

// Step number → slug mapping.
const stepNames = { 1: 'welcome', 2: 'configure', 3: 'features', 4: 'success' };

/**
 * Send the onboarding analytics blob to the server.
 *
 * @param {Object} data Analytics data with completed_steps, skipped_steps, etc.
 */
const saveOnboardingAnalytics = ( data ) => {
    const body = new URLSearchParams();
    body.append( 'action', 'hfe_save_onboarding_analytics' );
    body.append( 'nonce', hfe_admin_data.nonce );
    body.append( 'exited_early', data.exitedEarly ? 'true' : 'false' );
    body.append( 'exit_at_step', data.exitAtStep || '' );

    ( data.completedSteps || [] ).forEach( ( s ) => body.append( 'completed_steps[]', s ) );
    ( data.skippedSteps || [] ).forEach( ( s ) => body.append( 'skipped_steps[]', s ) );

    fetch( hfe_admin_data.ajax_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
    } ).catch( ( err ) => console.error( 'Onboarding analytics error:', err ) );
};

const Onboarding = () => {
    const [currentStep, setCurrentStep] = React.useState(() => {
        const savedStep = localStorage.getItem("currentStep");
        return savedStep ? parseInt(savedStep, 10) : 1;
    });

    // Track which steps were completed vs skipped across the journey.
    const stepsCompleted = useRef( JSON.parse( localStorage.getItem( 'hfe_onboarding_steps_completed' ) || '[]' ) );
    const stepsSkipped   = useRef( JSON.parse( localStorage.getItem( 'hfe_onboarding_steps_skipped' ) || '[]' ) );

    // Record a step as completed (localStorage only — no server call per step).
    const markStepCompleted = useCallback( ( stepName ) => {
        if ( ! stepsCompleted.current.includes( stepName ) ) {
            stepsCompleted.current.push( stepName );
            localStorage.setItem( 'hfe_onboarding_steps_completed', JSON.stringify( stepsCompleted.current ) );
        }
    }, [] );

    // Record a step as skipped (localStorage only).
    const markStepSkipped = useCallback( ( stepName ) => {
        if ( ! stepsSkipped.current.includes( stepName ) ) {
            stepsSkipped.current.push( stepName );
            localStorage.setItem( 'hfe_onboarding_steps_skipped', JSON.stringify( stepsSkipped.current ) );
        }
    }, [] );

    useEffect(() => {
        localStorage.setItem("currentStep", currentStep);
    }, [currentStep]);

    useEffect(() => {
        const timer = setTimeout(() => {
            localStorage.removeItem("currentStep");
        }, 180000); // 3 minutes

        return () => clearTimeout(timer);
    }, []);

    // "Exit Guided Setup" — send blob with early exit flag, then clean up.
    // Skip on step 4 (success) because the blob was already saved when Create mounted.
    const handleExit = () => {
        if ( currentStep >= 4 ) {
            return;
        }
        saveOnboardingAnalytics( {
            exitedEarly: true,
            exitAtStep: stepNames[ currentStep ] || '',
            completedSteps: stepsCompleted.current,
            skippedSteps: stepsSkipped.current,
        } );
        localStorage.removeItem( 'hfe_onboarding_steps_completed' );
        localStorage.removeItem( 'hfe_onboarding_steps_skipped' );
    };

    const StepComponent = allSteps[currentStep - 1]?.component;

    return (
        <div>
            <div className="w-full">
                <div className="flex flex-col items-center justify-center" style={{ marginTop: "-38px", marginBottom: "24px" }}>
                    <Topbar className="bg-none" style={{ background: "none" }}>
                        <Topbar.Left>
                            <Topbar.Item>
                                {hfeSettingsData.icon_svg && (
                                    <Link to={routes.dashboard.path}>
                                        <img
                                            src={`${hfeSettingsData.icon_new}`}
                                            alt="Logo"
                                            className="cursor-pointer"
                                            style={{ height: "35px" }}
                                        />
                                    </Link>
                                )}
                            </Topbar.Item>
                        </Topbar.Left>
                        <Topbar.Middle>
                            <Topbar.Item>
                                <ProgressSteps
                                    currentStep={Math.min(currentStep, visibleSteps.length)}
                                    className="uae-steps"
                                    variant="number"
                                    completedVariant="number"
                                >
                                    {visibleSteps.map((step, index) => (
                                        <ProgressSteps.Step
                                            key={index}
                                            className="font-bold"
                                            labelText={step.label}
                                            size="md"
                                        />
                                    ))}
                                </ProgressSteps>
                            </Topbar.Item>
                        </Topbar.Middle>
                        <Topbar.Right>
                            <Topbar.Item>
                                <Link
                                    className="hfe-remove-ring"
                                    to={routes.dashboard.path}
                                    onClick={handleExit}
                                >
                               <div className="flex items-center cursor-pointer justify-center gap-1">
                                 <p style={{ fontSize: '0.9rem', color: '#111827' }}>{__('Exit Guided Setup', 'header-footer-elementor')}</p>
                                    <Button
                                        icon={<X className="size-4" />}
                                        iconPosition="right"
                                        size="xs"
                                        variant="ghost"
                                        className="hfe-remove-ring"
                                    ></Button>
                               </div>
                                </Link>
                            </Topbar.Item>
                        </Topbar.Right>
                    </Topbar>
                </div>
            </div>

            <div className="flex items-center justify-center">
                {StepComponent && (
                    <StepComponent
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        saveOnboardingAnalytics={saveOnboardingAnalytics}
                        markStepCompleted={markStepCompleted}
                        markStepSkipped={markStepSkipped}
                        stepsCompleted={stepsCompleted}
                        stepsSkipped={stepsSkipped}
                    />
                )}
            </div>
        </div>
    );
};

export default Onboarding;
